import { Tables } from "@/lib/supabase/supabase_types_override";
import { isEqual } from "lodash";
import { extractFieldProperties } from "./extractFieldProperties";
import { InstanceFieldOptionsWouldBeLostChange } from "@/features/survey_sync/types/syncCheckResult";
import { FieldOptions } from "@/types/crm/tables/question";

export const checkInstanceFieldOptionsRisk = (
  question: Tables<"question">,
  templateQuestion: Tables<"survey_bank_question_bank">
): InstanceFieldOptionsWouldBeLostChange | false => {
  const { field_options: instanceFieldOptions } = extractFieldProperties(question);
  const { field_options: templateFieldOptions } = extractFieldProperties(templateQuestion);

  // If instance has no field options, no risk of loss
  if (!instanceFieldOptions) {
    return false;
  }

  // If template has no field options but instance does, all instance options would be lost
  if (!templateFieldOptions) {
    return {
      type: "INSTANCE_FIELD_OPTIONS_WOULD_BE_LOST",
      context: {
        instanceFieldOptions,
        templateFieldOptions,
        optionsThatWouldBeLost: instanceFieldOptions,
      },
    };
  }

  // Find options that exist in instance but not in template (would be lost)
  const optionsThatWouldBeLost = findOptionsThatWouldBeLost(instanceFieldOptions, templateFieldOptions);

  if (optionsThatWouldBeLost !== false) {
    return {
      type: "INSTANCE_FIELD_OPTIONS_WOULD_BE_LOST",
      context: {
        instanceFieldOptions,
        templateFieldOptions,
        optionsThatWouldBeLost,
      },
    };
  }

  return false;
};

function findOptionsThatWouldBeLost(
  instanceOptions: FieldOptions,
  templateOptions: FieldOptions
): FieldOptions | false {
  // Handle arrays (string[] or object[])
  if (Array.isArray(instanceOptions) && Array.isArray(templateOptions)) {
    const lostOptions = instanceOptions.filter((instanceOption) =>
      !templateOptions.some((templateOption) =>
        isEqual(instanceOption, templateOption)
      )
    );
    return lostOptions.length > 0 ? (lostOptions as FieldOptions) : false;
  }

  // Handle complex objects with options/groups properties
  if (
    typeof instanceOptions === "object" && instanceOptions !== null &&
    typeof templateOptions === "object" && templateOptions !== null
  ) {
    const lostItems: any[] = [];

    // Check for lost options
    if ("options" in instanceOptions && "options" in templateOptions) {
      const instanceOptionsList = instanceOptions.options;
      const templateOptionsList = templateOptions.options;

      if (Array.isArray(instanceOptionsList) && Array.isArray(templateOptionsList)) {
        const lostOptionItems = instanceOptionsList.filter((instanceOption) =>
          !templateOptionsList.some((templateOption) =>
            isEqual(instanceOption, templateOption)
          )
        );
        lostItems.push(...lostOptionItems);
      }
    }

    // Check for lost groups
    if ("groups" in instanceOptions && "groups" in templateOptions) {
      const instanceGroups = instanceOptions.groups;
      const templateGroups = templateOptions.groups;

      if (Array.isArray(instanceGroups) && Array.isArray(templateGroups)) {
        const lostGroups = instanceGroups.filter((instanceGroup) =>
          !templateGroups.some((templateGroup) =>
            isEqual(instanceGroup, templateGroup)
          )
        );
        lostItems.push(...lostGroups);
      }
    }

    // Check for lost simple properties
    Object.keys(instanceOptions).forEach((key) => {
      if (!(key in templateOptions)) {
        const instanceValue = (instanceOptions as Record<string, any>)[key];
        if (instanceValue != null) {
          lostItems.push({ [key]: instanceValue });
        }
      }
    });

    return lostItems.length > 0 ? lostItems : false;
  }

  return false;
}